import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT, SLIDE_FEATHER_ICON} from '#/lib/slideTokens'
import {type SlideEditorProps} from '#/components/capsules/play/types'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {type FillBlank, SLIDE_TYPE_META} from '#/types/capsule'

function uid() {
  return Math.random().toString(36).slice(2)
}

export function FillBlankSlideEditor({
  slide,
  onChange,
  primaryColor: _primaryColor,
  onPreview,
}: SlideEditorProps) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const accent = SLIDE_ACCENT.fill_blank
  // Ink, not fill: the taxonomy's colour tuned to read on the studio surface it
  // is written on (see `readableAccent`). Tints stay on the raw hue.
  const accentInk = readableAccent(accent, chrome)
  const meta = SLIDE_TYPE_META.fill_blank
  const slideIcon = SLIDE_FEATHER_ICON.fill_blank ?? 'help-circle'
  const blanks: FillBlank[] = slide.blanks ?? []

  const updateBlank = (id: string, patch: Partial<FillBlank>) => {
    onChange({blanks: blanks.map(b => (b.id === id ? {...b, ...patch} : b))})
  }

  const addBlank = () => {
    onChange({
      blanks: [...blanks, {id: uid(), before: '', answer: '', after: ''}],
    })
  }

  const removeBlank = (id: string) => {
    onChange({blanks: blanks.filter(b => b.id !== id)})
  }

  return (
    <ScrollView
      style={ss.root}
      contentContainerStyle={ss.inner}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}>
      {/* Host-provided header (e.g. FlashBook studio) replaces this row */}
      <View style={[ss.topRow, onPreview ? null : {display: 'none'}]}>
        <View
          style={[
            ss.typeBadge,
            {backgroundColor: accent + '22', borderColor: accent + '44'},
          ]}>
          <Feather name={slideIcon} size={12} color={accentInk} />
          <Text style={[ss.typeBadgeText, {color: accentInk}]}>
            {meta?.label ?? slide.type}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onPreview}
          hitSlop={6}
          style={[
            ss.previewBtn,
            {backgroundColor: accent + '18', borderColor: accent + '55'},
          ]}>
          <Feather name="play-circle" size={13} color={accentInk} />
          <Text
            style={[ss.previewBtnText, {color: accentInk}]}>{l`Preview`}</Text>
        </TouchableOpacity>
      </View>

      <StudioTextField
        accessibilityLabel="Text input field"
        accessibilityHint={undefined}
        style={ss.question}
        value={slide.question ?? ''}
        onChangeText={v => onChange({question: v})}
        placeholder={l`Écris ta question ici…`}
        placeholderTextColor={chrome.textFaint}
      />

      <Text style={ss.sectionLabel}>{l`BLANKS`}</Text>

      {blanks.map((blank, idx) => (
        <View
          key={blank.id}
          style={[ss.card, {borderColor: chrome.borderStrong}]}>
          <View style={ss.cardHeader}>
            <View style={[ss.numBadge, {backgroundColor: accent}]}>
              <Text style={ss.numBadgeText}>{idx + 1}</Text>
            </View>
            <TouchableOpacity
              accessibilityRole="button"
              hitSlop={6}
              onPress={() => removeBlank(blank.id)}>
              <Feather name="trash-2" size={13} color={chrome.textFaint} />
            </TouchableOpacity>
          </View>
          <TextInput
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={ss.sentenceInput}
            value={blank.before}
            onChangeText={v => updateBlank(blank.id, {before: v})}
            placeholder={l`Text before blank…`}
            placeholderTextColor={chrome.textFaint}
          />
          <TextInput
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={[ss.sentenceInput, {borderColor: accent + '66'}]}
            value={blank.answer}
            onChangeText={v => updateBlank(blank.id, {answer: v})}
            placeholder={l`Correct answer…`}
            placeholderTextColor={chrome.textFaint}
          />
          <TextInput
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={ss.sentenceInput}
            value={blank.after}
            onChangeText={v => updateBlank(blank.id, {after: v})}
            placeholder={l`Text after blank…`}
            placeholderTextColor={chrome.textFaint}
          />
        </View>
      ))}

      <TouchableOpacity
        accessibilityRole="button"
        onPress={addBlank}
        style={[
          ss.addBtn,
          {borderColor: accent + '66', backgroundColor: accent + '11'},
        ]}>
        <Feather name="plus" size={16} color={accentInk} />
        <Text style={[ss.addTxt, {color: accentInk}]}>{l`+ Add a blank`}</Text>
      </TouchableOpacity>

      <View style={ss.explanationWrap}>
        <Text style={ss.explanationLabel}>
          {l`EXPLICATION (optionnelle)`.toUpperCase()}
        </Text>
        <View style={ss.explanationCard}>
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={ss.explanationInput}
            value={slide.explanation ?? ''}
            onChangeText={v => onChange({explanation: v})}
            placeholder={l`Explication affichée après la réponse…`}
            placeholderTextColor={chrome.textFaint}
            textAlignVertical="top"
          />
        </View>
      </View>
    </ScrollView>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: chrome.frame},
    inner: {padding: 20, paddingBottom: 40, gap: 16},
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    typeBadgeText: {fontSize: 11, fontWeight: '700'},
    previewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    previewBtnText: {fontSize: 11, fontWeight: '700'},
    question: {
      color: chrome.text,
      fontSize: 20,
      fontWeight: '700',
      lineHeight: 28,
      paddingVertical: 0,
    },
    sectionLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    card: {
      backgroundColor: chrome.fill,
      borderWidth: 1.5,
      borderRadius: 12,
      padding: 12,
      gap: 8,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    numBadge: {
      width: 22,
      height: 22,
      borderRadius: 6,
      alignItems: 'center',
      justifyContent: 'center',
    },
    numBadgeText: {color: chrome.onAccent, fontSize: 11, fontWeight: '800'},
    sentenceInput: {
      color: chrome.textMuted,
      fontSize: 14,
      backgroundColor: chrome.fill,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
    },
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingVertical: 14,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
    },
    addTxt: {fontSize: 14, fontWeight: '600'},
    explanationWrap: {gap: 6},
    explanationLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    explanationCard: {
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    explanationInput: {
      color: chrome.textMuted,
      fontSize: 13,
      lineHeight: 18,
      paddingVertical: 0,
    },
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
